/**
 * Profile: what the player told us (nickname, grade, school, place, whether
 * they're on the leaderboards) with Edit, the theme gallery (every level's
 * theme, unlocked by reaching that level in a run) and the theme sticker
 * album. The only rewarded-ad entry point lives here, opt-in and cosmetic
 * only (docs/MONETIZATION.md § 2): it earns stickers for themes the player
 * has already reached, never a theme ahead of play.
 */
import { useState } from 'react';
import { THEMES, getTheme, type Locale } from '@dropstack/core';
import type { PlayerProfile } from '../contracts.ts';
import { useT } from '../i18n/index.ts';
import { countryName, provinceName } from '../services/geo.ts';
import type { RewardedOutcome } from '../services/ads.ts';
import { Button } from '../components/Button.tsx';
import { Icon } from '../components/Icon.tsx';
import { ScreenLayout, Section } from '../components/ScreenLayout.tsx';
import { StickerShelf } from '../components/Sticker.tsx';
import { ThemeSwatch } from '../theme/index.ts';
import { nextStickerId, type OnboardingStepId } from './model/onboarding.ts';
import './onboarding.css';

export interface ProfileScreenProps {
  profile: PlayerProfile | null;
  stickers: readonly string[];
  /** Progress.highestLevelReached: themes up to this level are unlocked. */
  highestLevelReached: number;
  locale: Locale;
  /** Ads are on (the rewarded button is offered only then). */
  adsEnabled: boolean;
  rewardedLeftToday: number;
  onEdit(step: OnboardingStepId): void;
  onJoin(): void;
  /** Show the rewarded (sample) ad; resolves with what happened. */
  onEarnSticker(): Promise<RewardedOutcome>;
  /** Called with the theme id to add when a rewarded ad completed. */
  onStickerEarned(themeId: string): void;
  onBack(): void;
}

export function ProfileScreen(props: ProfileScreenProps) {
  const { profile, stickers, locale, onEdit, onJoin, onBack } = props;
  const t = useT();
  const onboarded = Boolean(profile?.onboarded);

  return (
    <ScreenLayout title={t('shell.profTitle')} onBack={onBack} backLabel={t('common.back')} className="profile">
      {onboarded && profile ? (
        <Section title={profile.displayName} icon={<Icon name="user" />} className="profile__card">
          <dl className="profile__facts">
            <Fact label={t('shell.profGrade')} value={profile.grade === null ? '–' : t('common.gradeN', { grade: profile.grade })} onEdit={() => onEdit('grade')} />
            <Fact label={t('shell.profSchool')} value={profile.schoolName || '–'} onEdit={() => onEdit('school')} />
            <Fact label={t('shell.profPlace')} value={placeText(profile, locale)} onEdit={() => onEdit('school')} />
            <Fact
              label={t('shell.profBoards')}
              value={profile.leaderboardOptIn ? t('shell.profBoardsOn') : t('shell.profBoardsOff')}
              onEdit={() => onEdit('leaderboard')}
            />
          </dl>
          <Button icon="pencil" onClick={() => onEdit('name')}>
            {t('shell.profEdit')}
          </Button>
        </Section>
      ) : (
        <Section title={t('shell.profNewTitle')} icon={<Icon name="user" />} className="profile__card">
          <p className="profile__text">{t('shell.profNewText')}</p>
          <Button variant="primary" icon="trophy" onClick={onJoin}>
            {t('shell.goJoinButton')}
          </Button>
        </Section>
      )}

      <ThemeGallery highestLevelReached={props.highestLevelReached} locale={locale} />

      <Section title={t('shell.profStickers')} icon={<Icon name="sticker" />} className="profile__stickers">
        <p className="profile__text">{t('shell.profStickerCount', { count: stickers.length, total: THEMES.length })}</p>
        <StickerShelf collected={stickers} locale={locale} label={t('shell.profStickers')} />
        <EarnSticker {...props} />
      </Section>
    </ScreenLayout>
  );
}

function Fact({ label, value, onEdit }: { label: string; value: string; onEdit(): void }) {
  const t = useT();
  return (
    <div className="profile__fact">
      <dt>{label}</dt>
      <dd>
        <span>{value}</span>
        <button type="button" className="profile__edit" onClick={onEdit} aria-label={t('shell.profEditField', { field: label })}>
          <Icon name="pencil" />
        </button>
      </dd>
    </div>
  );
}

function placeText(profile: PlayerProfile, locale: Locale): string {
  const parts = [profile.city, profile.province ? provinceName(profile.province, locale) : '', countryName(profile.country, locale)];
  return parts.filter(Boolean).join(', ') || '–';
}

/** Width of a gallery thumbnail in CSS px (two cards fit side by side on a 320 px phone). */
const SWATCH_WIDTH = 100;

/** Every level's theme: reached ones in colour, the rest dimmed with a lock. */
function ThemeGallery({ highestLevelReached, locale }: { highestLevelReached: number; locale: Locale }) {
  const t = useT();
  const unlocked = THEMES.filter((theme) => theme.level <= highestLevelReached).length;
  return (
    <Section title={t('shell.profThemes')} icon={<Icon name="sparkle" />} className="profile__themes">
      <p className="profile__text">{t('shell.profThemesCount', { count: unlocked, total: THEMES.length })}</p>
      <p className="profile__note">{t('shell.profThemesHint')}</p>
      <ul className="profile__gallery" aria-label={t('shell.profThemes')}>
        {THEMES.map((theme) => {
          const locked = theme.level > highestLevelReached;
          return (
            <li key={theme.id} className="profile__gallery-item">
              <ThemeSwatch
                theme={theme}
                locale={locale}
                width={SWATCH_WIDTH}
                locked={locked}
                lockedLabel={locked ? t('shell.profThemeLocked') : undefined}
              />
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

/** The opt-in rewarded ad: earns a sticker for the next theme the player has reached. */
function EarnSticker({ stickers, highestLevelReached, locale, adsEnabled, rewardedLeftToday, onEarnSticker, onStickerEarned }: ProfileScreenProps) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const next = nextStickerId(stickers, highestLevelReached);

  if (!adsEnabled) return null;
  if (!next) {
    const all = THEMES.every((theme) => stickers.includes(theme.id));
    return <p className="profile__text">{t(all ? 'shell.profAllStickers' : 'shell.profStickersReachMore')}</p>;
  }

  const earn = async () => {
    setBusy(true);
    setMessage(null);
    const outcome = await onEarnSticker();
    setBusy(false);
    if (outcome === 'completed') {
      onStickerEarned(next);
      setMessage(t('shell.profStickerEarned', { name: getTheme(next).name[locale] }));
    } else if (outcome === 'unavailable') {
      setMessage(t('shell.profStickerUnavailable'));
    }
  };

  return (
    <div className="profile__earn">
      {rewardedLeftToday > 0 ? (
        <Button icon="sparkle" busy={busy} onClick={() => void earn()}>
          {t('shell.profEarnSticker')}
        </Button>
      ) : (
        <p className="profile__text">{t('shell.profStickerTomorrow')}</p>
      )}
      <p className="profile__note">{t('shell.profEarnNote')}</p>
      <p className="profile__status" role="status">
        {message ?? ''}
      </p>
    </div>
  );
}
